import { Component, Input, SimpleChanges } from '@angular/core';
import {
    trigger,
    state,
    style,
    transition,
    animate,
} from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { CountryCodeService } from 'src/app/services/country-code.service';

@Component({
    selector: 'app-passengers-info',
    templateUrl: './passengers-info.component.html',
    styleUrls: ['./passengers-info.component.scss'],
    animations: [
        trigger('slideDownUp', [
            state('void', style({ height: '0px', opacity: 0 })),
            state('*', style({ height: '*', opacity: 1 })),
            transition(':enter', [animate('300ms ease-out')]),
            transition(':leave', [animate('300ms ease-in')]),
        ]),
        trigger('slideInOut', [
            state('void', style({ opacity: 0 })),
            state('*', style({ opacity: 1 })),
            transition(':enter', [animate('300ms ease-in')]),
            transition(':leave', [animate('300ms ease-out')]),
        ]),
    ],
})
export class PassengersInfoComponent {
    @Input() currentModeView: string = 'DEPART';
    @Input() flightDetail: any;
    @Input() passengerList: any[];
    @Input() isModeBookingIssue: boolean = false;
    @Input() isShowViewDetail: boolean = false;

    isExpand: boolean = false;
    isMouseOverExpand: boolean = false;
    totalAdults: number = 0;
    totalChildren: number = 0;
    totalInfant: number = 0;
    isShowChooseSchedulePopup = false;
    isShowViewDetails: boolean = false;
    requestDetail: any;
    viewDetailID: any
    changeRequestList: any[] = [];
    historyList: any[] = [];
    chosenSchedule: any;
    typeViewDetails: string = '';
    isInitial: boolean = false;

    constructor(
        private translateService: TranslateService,
        private countryCodeService: CountryCodeService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['currentModeView'] && changes['currentModeView'].currentValue && this.isInitial) {
            this.initTotalData();
            // Handle format change request status
            this.initData();
        }
        if (changes['flightDetail'] && changes['flightDetail'].currentValue) {
            this.initTotalData();
            this.changeRequestList = [];
            this.initData();
            this.isInitial = true;
        }
    }

    initTotalData() {
        this.totalAdults = this.countPassengerByType(this.passengerList, ['ADULT', 'ADT']);
        this.totalChildren = this.countPassengerByType(this.passengerList, ['CHILD', 'CHD', 'CHILDREN']);
        this.totalInfant = this.countPassengerByType(this.passengerList, ['INFANT', 'INF ']);
    }

    get passengerSummary(): string {
        const parts: string[] = [];

        if (this.totalAdults > 0) {
            parts.push(
                `${this.totalAdults} ` +
                this.translateService
                    .instant('FLIGHT.PASSENGERS.ADULT', { syntax: this.totalAdults > 1 ? 's' : '' })
                    .toLowerCase()
            );
        }

        if (this.totalChildren > 0) {
            parts.push(
                `${this.totalChildren} ` +
                this.translateService
                    .instant('FLIGHT.PASSENGERS.CHILD', { syntax: this.totalChildren > 1 ? 's' : '' })
                    .toLowerCase()
            );
        }

        if (this.totalInfant > 0) {
            parts.push(
                `${this.totalInfant} ` +
                this.translateService
                    .instant('FLIGHT.PASSENGERS.INFANT', { syntax: this.totalInfant > 1 ? 's' : '' })
                    .toLowerCase()
            );
        }
        return parts.length ? `(${parts.join(', ')})` : '';
    }

    initData() {
        // Handle format change request status
        const index = this.currentModeView == 'DEPART' ? 0 : 1;
        if (this.isModeBookingIssue && !this.changeRequestList[index]?.length) {
            this.passengerList.forEach((passenger: any) => {
                if (Array.isArray(passenger.change_request_history)) {
                    const changeRequestHistory: any[] = passenger.change_request_history;
                    let primaryStatus = '';
                    const uniqueStatusesSet = new Set<string>();

                    for (const item of changeRequestHistory) {
                        const status = item.flight_issue_type?.toUpperCase();
                        if (status !== 'FINDING_FLIGHT') uniqueStatusesSet.add(status);

                        if (status === 'CANCEL_FLIGHT') {
                            primaryStatus = item.flight_issue_type;
                        } else if (!primaryStatus && status !== 'FINDING_FLIGHT') {
                            primaryStatus = item.flight_issue_type;
                        }
                    }

                    this.changeRequestList[index] = this.changeRequestList[index]?.length ? this.changeRequestList[index] : [];
                    this.changeRequestList[index].push({
                        history: changeRequestHistory,
                        primaryStatus: primaryStatus,
                        uniqueStatuses: Array.from(uniqueStatusesSet)
                    });
                }
            })
        }
    }

    countPassengerByType(passengerList: any[], types: string[]) {
        return passengerList.filter(
            (passenger) => types.includes(passenger.passenger_type?.toUpperCase())
        ).length;
    }

    handleClickExpandCollapse() {
        this.isExpand = !this.isExpand;
    }

    onCloseChooseSchedulePopup() {
        this.chosenSchedule = null;
        this.isShowChooseSchedulePopup = false;
    }

    onShowChooseSchedulePopup(history: any) {
        if (history.length === 1 && history?.[0]?.flight_issue_type !== 'FINDING_FLIGHT') {
            this.chosenSchedule = history[0];
            this.handleConfirmChooseSchedule();
        } else {
            this.historyList = history;
            this.isShowChooseSchedulePopup = true;
        }
    }

    handleConfirmChooseSchedule() {
        if (this.chosenSchedule.flight_issue_type === 'CANCEL_FLIGHT') {
            this.typeViewDetails = 'CANCEL';
        } else if (this.chosenSchedule.flight_issue_type === 'RESCHEDULE_FLIGHT') {
            this.typeViewDetails = 'RESCHEDULE';
        } else if (this.chosenSchedule.flight_issue_type === 'CHANGE_PASSENGER_NAME') {
            this.typeViewDetails = 'CHANGE_NAME';
        } else if (this.chosenSchedule.flight_issue_type === 'ADDITIONAL_PURCHASING') {
            this.typeViewDetails = 'ADD_ONS';
        } else {
            this.typeViewDetails = 'FINDING_FLIGHT';
        }
        this.isShowChooseSchedulePopup = false;
        this.isShowViewDetails = true;
    }

    handleCloseViewDetailsPopup() {
        this.chosenSchedule = null;
        this.isShowViewDetails = false;
    }

    getRequestStatus(status: string): string {
        switch (status?.toUpperCase()) {
            case "RESCHEDULE_FLIGHT":
                return 'BOOKING_ISSUE.RESCHEDULED';
            case "CHANGE_PASSENGER_NAME":
                return 'BOOKING_ISSUE.CHANGED_NAME';
            case "ADDITIONAL_PURCHASING":
                return 'BOOKING_ISSUE.PURCHASED_SERVICE';
            case "CANCEL_FLIGHT":
                return 'BOOKING_ISSUE.CANCELED';
            default:
                return '';
        }
    }

    getUTCUser(): string {
        return this.countryCodeService.getUTCOffset();
    }
}
